import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout';
import SEO from '@/components/shared/SEO';

const TermsOfServicePage = () => {
  const lastUpdated = 'April 7, 2026';

  return (
    <Layout>
      <SEO
        title="Terms of Service"
        description="Terms and Conditions governing your use of the Portico Systems website and services."
        canonicalUrl="/terms"
      />

      <section className="bg-primary-bg text-primary-foreground py-12 md:py-16">
        <div className="container-portico">
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl md:text-5xl font-bold"
          >
            Terms of Service
          </motion.h1>
          <p className="mt-3 text-sm opacity-70">Last updated: {lastUpdated}</p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container-portico max-w-3xl">
          <div className="prose prose-gray max-w-none space-y-8 text-foreground/90">

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">1. Acceptance of Terms</h2>
              <p className="leading-relaxed">
                By accessing and using the Portico Systems website ("Site"), you accept and agree to be bound by these Terms of Service ("Terms"). If you do not agree with any part of these Terms, you must not use the Site. These Terms apply to all visitors, users, and others who access or use the Site.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">2. Use of the Site</h2>
              <p className="leading-relaxed mb-2">You agree to use the Site only for lawful purposes and in accordance with these Terms. You agree not to:</p>
              <ul className="list-disc pl-6 space-y-1.5 text-sm">
                <li>Use the Site in any way that violates applicable federal, state, local, or international law</li>
                <li>Submit false, misleading, or fraudulent information through any forms</li>
                <li>Attempt to gain unauthorized access to any part of the Site or its related systems</li>
                <li>Introduce viruses, malware, or other harmful material</li>
                <li>Engage in any activity that interferes with or disrupts the Site's functionality</li>
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">3. Products &amp; Services</h2>
              <p className="leading-relaxed">
                Portico Systems provides commercial flooring products and related services. Product images, descriptions, specifications, and pricing on the Site are for informational purposes and may vary from actual products. We reserve the right to modify product offerings, availability, and pricing at any time without prior notice.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">4. Quotes &amp; Orders</h2>
              <p className="leading-relaxed">
                Quotes provided through the Site are estimates and are subject to change based on final measurements, product availability, and project requirements. A formal agreement or purchase order must be executed before any order is considered binding. Portico Systems reserves the right to accept or decline any order at its sole discretion.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">5. Intellectual Property</h2>
              <p className="leading-relaxed">
                All content on the Site — including text, images, logos, graphics, product designs, and software — is the property of Portico Systems or its licensors and is protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works of any content without our express written permission.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">6. Sample Requests</h2>
              <p className="leading-relaxed">
                Samples are provided free of charge for evaluation purposes only. Portico Systems reserves the right to limit the number of samples per request. Samples may differ slightly from production materials in color and texture. Sample availability is subject to stock.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">7. Consultation Scheduling</h2>
              <p className="leading-relaxed">
                Consultation appointments booked through the Site are subject to availability. We will make reasonable efforts to honor scheduled appointments, but reserve the right to reschedule with advance notice. Consultations are complimentary and do not constitute a binding commitment to purchase.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">8. Limitation of Liability</h2>
              <p className="leading-relaxed">
                To the fullest extent permitted by law, Portico Systems shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Site. Our total liability for any claim arising from or relating to these Terms shall not exceed the amount paid by you, if any, for accessing the Site.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">9. Disclaimer of Warranties</h2>
              <p className="leading-relaxed">
                The Site is provided on an "as is" and "as available" basis without warranties of any kind, whether express or implied, including but not limited to the implied warranties of merchantability, fitness for a particular purpose, and non-infringement. Portico Systems does not warrant that the Site will be uninterrupted, error-free, or secure.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">10. Indemnification</h2>
              <p className="leading-relaxed">
                You agree to indemnify, defend, and hold harmless Portico Systems, its officers, directors, employees, and agents from and against any claims, damages, obligations, losses, liabilities, and expenses arising from your use of the Site or violation of these Terms.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">11. Governing Law</h2>
              <p className="leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the State of South Carolina, without regard to its conflict of law provisions. Any legal action or proceeding arising under these Terms shall be brought exclusively in courts located in South Carolina.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">12. Changes to These Terms</h2>
              <p className="leading-relaxed">
                We reserve the right to update these Terms at any time. Changes will be posted on this page with a revised "Last Updated" date. Your continued use of the Site after changes are posted constitutes acceptance of the revised Terms. We encourage you to review this page periodically.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">13. Contact Us</h2>
              <p className="leading-relaxed">
                If you have questions about these Terms, please contact us:
              </p>
              <div className="mt-3 bg-muted rounded-xl p-5 text-sm space-y-1">
                <p className="font-semibold">Portico Systems</p>
                <p>Email: <a href="mailto:info@porticosystems.com" className="text-primary hover:underline">info@porticosystems.com</a></p>
                <p>Phone: <a href="tel:1-864-527-3148" className="text-primary hover:underline">1-864-527-3148</a></p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TermsOfServicePage;
